import React, { useState, useEffect, useRef } from "react";
import DataTable from "@/common/elements/DataTable";
import {
  StyledFlex,
  StyledGridCol,
  StyledGridRow,
  StyledSectionHeading,
  StyledSubTableContainer,
} from "@/common/styles";
import {
  getDayjsObject,
  getFormattedDayjsObjectToString,
  getOptionsFromRefList,
  getRefListNameByID,
  isNonEmptyVariable,
} from "@/util/common_function";
import Button from "@/common/elements/Button";
import Modal from "@/common/elements/Modal";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import CommonDatePicker from "@/common/elements/DatePicker";
import { Form } from "antd";
import Select from "@/common/elements/InputElements/Select";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import TextField from "@/common/elements/InputElements/TextField";
import { v4 as uuidV4 } from "uuid";
import useDialog from "@/hooks/useDialog";
import { setDeleteAPis } from "@/slices/bookingsSlice";
import { useDispatch, useSelector } from "react-redux";
import API_ENDPOINTS from "@/util/api";

const JailIncidentIReviewHearings = ({
  data,
  setReviewHearingData,
  isFieldEnable,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const dataTableRef = useRef();
  const { showDialog } = useDialog();
  const [showModal, setShowModal] = useState(false);
  const [tableData, setTableData] = useState({
    headers: [
      { key: "id", text: "ID" },
      { key: "dueDate", text: "Due Date" },
      { key: "dueTime", text: "Due Time" },
      { key: "actualDate", text: "Actual Date" },
      { key: "actualTime", text: "Actual Time" },
      { key: "supervisor", text: "Supervisor" },
      { key: "jailIncidentsResults", text: "JaillncidentsResults" },
      { key: "appeal", text: "Appeal" },
      { key: "comments", text: "Comments" },
    ],
    rows: [],
  });
  const [selectedReviewHearingData, setSelectedReviewHearingData] =
    useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isModify, setisModify] = useState(false);

  useEffect(() => {
    if (isNonEmptyVariable(data) && data?.length) {
      const rows = data?.map((row, index) => ({
        uuid: uuidV4(),
        id: row?.jailIncidentsInvolvedInmateReviewHearingId || "",
        dueDate: row?.dueDate,
        dueTime: row?.dueTime,
        actualDate: row?.actualDate,
        actualTime: row?.actualTime,
        supervisor: getRefListNameByID("", row?.supervisorStaffId, true, true),
        supervisorStaffId: row?.supervisorStaffId,
        jailIncidentsResults: row?.jailIncidentsResults,
        appeal: row?.appeal ? "Yes" : "No",
        comments: row?.comments,
      }));
      setTableData((prev) => ({ ...prev, rows }));
    } else {
      setTableData((prev) => ({ ...prev, rows: [] }));
    }
  }, [data]);

  const handleAction = (action) => {
    switch (action) {
      case "add":
        setisModify(true);
        setEditingIndex(null);
        setShowModal(true);
        break;
      case "edit":
        setisModify(true);
        setShowModal(true);

        form.setFieldsValue({
          ...selectedReviewHearingData,
          dueDateTime: getDayjsObject(
            selectedReviewHearingData?.dueDate,
            selectedReviewHearingData?.dueTime,
          ),
          actualDateTime: getDayjsObject(
            selectedReviewHearingData?.actualDate,
            selectedReviewHearingData?.actualTime,
          ),
        });
        break;
      case "view":
        setisModify(false);
        setShowModal(true);
        break;
      case "delete":
        showDialog("confirm", {
          title: "Delete Action",
          content: `When you save this record, the object will be deleted. 
If you change your mind about the deleting the object, 
click Cancel in the Action Pane and the object will not be deleted.
`,
          onConfirm: () => deleteReviewHearing(),
          oncancel: () => onCloseModal(),
        });
        break;
      default:
        return;
    }
  };

  const handleRowClick = (value, index) => {
    if (!isNonEmptyVariable(value?.uuid)) {
      return;
    }

    setSelectedReviewHearingData(value);
    setEditingIndex(index);
  };

  const handleSubmit = () => {
    form?.validateFields().then((values) => {
      const _dueDateTime = getFormattedDayjsObjectToString(values?.dueDateTime);
      const _actualDateTime = getFormattedDayjsObjectToString(
        values?.actualDateTime,
      );
      const rows = tableData?.rows?.length ? tableData?.rows : [];

      const modifieValues = {
        ...values,
        dueDate: isNonEmptyVariable(_dueDateTime)
          ? _dueDateTime?.split(" ")?.[0]
          : null,
        dueTime: isNonEmptyVariable(_dueDateTime)
          ? _dueDateTime?.split(" ")?.[1]
          : null,
        actualDate: isNonEmptyVariable(_actualDateTime)
          ? _actualDateTime?.split(" ")?.[0]
          : null,
        actualTime: isNonEmptyVariable(_actualDateTime)
          ? _actualDateTime?.split(" ")?.[1]
          : null,
      };
      if (editingIndex !== null) {
        rows[editingIndex] = {
          ...rows?.[editingIndex],
          ...modifieValues,
        };
      } else {
        rows.push({
          ...modifieValues,
        });
      }

      setReviewHearingData?.(() => rows);
      onCloseModal();
    });
  };

  const onCloseModal = () => {
    dataTableRef?.current?.resetAll();
    form.resetFields();
    setisModify(false);
    setSelectedReviewHearingData(null);
    setEditingIndex(null);
    setShowModal(false);
  };

  const deleteReviewHearing = () => {
    const _reviewHearingData = data?.filter(
      (_, index) => index !== editingIndex,
    );

    setReviewHearingData(() => _reviewHearingData);

    if (isNonEmptyVariable(selectedReviewHearingData?.id)) {
      const url =
        API_ENDPOINTS.jailIncidents.deleteReviewHearing +
        selectedReviewHearingData?.id;
      dispatch(setDeleteAPis(url));
    }

    onCloseModal();
  };

  return (
    <>
      <StyledSectionHeading marginTop="20px">
        <span>Review Hearings</span>
      </StyledSectionHeading>

      <StyledSubTableContainer>
        <StyledFlex align="center" justify="space-between">
          <h2>List of Jail Incidents Review Hearings</h2>

          <StyledFlex marginBottom="10px" gap="10px">
            <Button
              onClick={() => handleAction("add")}
              disabled={!isFieldEnable}
            >
              Add
            </Button>
            <Button
              onClick={() => handleAction("view")}
              disabled={selectedReviewHearingData === null}
            >
              Details
            </Button>
            <Button
              onClick={() => handleAction("edit")}
              disabled={selectedReviewHearingData === null || !isFieldEnable}
            >
              Modify
            </Button>
            <Button
              onClick={() => handleAction("delete")}
              disabled={selectedReviewHearingData === null || !isFieldEnable}
            >
              Delete
            </Button>
          </StyledFlex>
        </StyledFlex>

        <DataTable
          ref={dataTableRef}
          headers={tableData.headers}
          data={tableData.rows || []}
          rowUniqueId="uuid"
          onRowClick={handleRowClick}
          selected={selectedReviewHearingData === null}
        />
      </StyledSubTableContainer>

      {showModal && (
        <Modal
          show={showModal}
          header="Jail Incident Review Hearing"
          handleCancel={onCloseModal}
          handleSubmit={handleSubmit}
          hideSubmitButton={!isModify || !isFieldEnable}
          onHide={onCloseModal}
        >
          <Form form={form}>
            <StyledGridRow gutter={16}>
              <StyledGridCol lg={8}>
                <FormItem label="Due Date/Time" name="dueDateTime">
                  <CommonDatePicker
                    showTime={true}
                    format="MM/DD/YYYY HH:mm"
                    disabled={!isModify || !isFieldEnable}
                  />
                </FormItem>
              </StyledGridCol>

              <StyledGridCol lg={8}>
                <FormItem label="Actual Date/Time" name="actualDateTime">
                  <CommonDatePicker
                    showTime={true}
                    format="MM/DD/YYYY HH:mm"
                    disabled={!isModify || !isFieldEnable}
                  />
                </FormItem>
              </StyledGridCol>

              <StyledGridCol lg={8}>
                <FormItem label="Supervisor" name="supervisorStaffId">
                  <Select
                    options={getOptionsFromRefList("", true, true, true)}
                    disabled={!isModify || !isFieldEnable}
                  />
                </FormItem>
              </StyledGridCol>
            </StyledGridRow>

            <StyledGridRow gutter={16}>
              <StyledGridCol lg={8}>
                <FormItem label="Result" name="jailIncidentsResults">
                  <Select
                    options={getOptionsFromRefList("JailIncidentsResults")}
                    disabled={!isModify || !isFieldEnable}
                  />
                </FormItem>
              </StyledGridCol>

              <StyledGridCol lg={8}>
                <FormItem
                  label="Appeal"
                  name="appeal"
                  layout="horizontal"
                  labelPostion="label-right"
                  paddingTop="26px"
                  valuePropName="checked"
                >
                  <Checkbox disabled={!isModify || !isFieldEnable} />
                </FormItem>
              </StyledGridCol>
            </StyledGridRow>

            <StyledGridRow gutter={16}>
              <StyledGridCol lg={24}>
                <FormItem label="Comments" name="comments">
                  <TextField
                    inputType="textarea"
                    rows={4}
                    disabled={!isModify || !isFieldEnable}
                  />
                </FormItem>
              </StyledGridCol>
            </StyledGridRow>
          </Form>
        </Modal>
      )}
    </>
  );
};

export default JailIncidentIReviewHearings;
